/**
 * De middenkolom van Vandaag: klantblok, De zaak, bericht van de klant.
 *
 * Volgorde ligt vast (ontwerp v4 §3): (a) klantblok, (b) De zaak met
 * knop Acties, (c) Bericht van de klant. De reactietegel staat eronder
 * en is van bouwer 2; die zit in de schil, niet hier.
 *
 * Twee dingen die hier bewust zó zijn:
 *
 * - **Panelen schuiven over de middenkolom.** "Lees het origineel" en
 *   "Eerdere gesprekken" liggen als `position: absolute` over deze
 *   kolom, met eigen scroll en een kruisje. Nooit een laag over het
 *   hele scherm, en de kolom wordt er nooit langer van. Hetzelfde geldt
 *   voor het vergroten van een foto.
 * - **Een leeg blok zegt waarom het leeg is.** Geen orderregels? Dan
 *   staat er dat ze niet bekend zijn. Geen gewicht? Dan staat er dat
 *   Picqer het niet levert. Nooit een nul die op een meting lijkt.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { adminRetourenApi } from "@/lib/api";
import { openPdf } from "@/lib/pdf-openen";

import {
  kaartenApi,
  werkplekApi,
  werkplekKlantenApi,
  type TijdlijnRegel,
  type ZaakDetail,
  type ZaakTelefoon,
} from "@/lib/werkplek-api";

import { DierenBeheer } from "../Klanten/DierenBeheer";
import { VoedingsBlok } from "../Klanten/VoedingsBlok";
import { useKlantActies, type KlantKanaal } from "../Tegel/klantActies";
import "../Klanten/klantpagina.css";

import { ActiesMenu } from "./ActiesMenu";
import { KoppelBanner } from "./KoppelBanner";
import { BallonExtra, SchadeBlok, VerzendStatus } from "./Schadedossier";
import { useKoppelBanner } from "./koppelbannerVraag";
import {
  IcoonBeeld,
  IcoonExtern,
  IcoonKruis,
  IcoonMail,
  IcoonTelefoon,
  IcoonWhatsapp,
} from "./pictogrammen";
import { datum, datumTijd, euro, klok } from "./tijd";
import { wekkerReden } from "./wekkerTekst";
import "./vandaag.css";

type PaneelSoort = "origineel" | "eerder" | "voeding" | null;

/**
 * Welke blokken deze kolom zelf tekent.
 *
 * Retouren en Reviews zetten hun eigen blok boven deze kolom. Zonder
 * deze schakelaars stonden er twee blokken met elk een eigen
 * Acties-menu onder elkaar, en zei het tweede blok "Geen order
 * gekoppeld" over een retour die het ordernummer gewoon draagt (JP,
 * punten 31 en 33 van 22-09).
 */
export interface MiddenkolomDelen {
  kop?: boolean;
  klantblok?: boolean;
  zaakblok?: boolean;
  /**
   * Toont het blok "Bericht van de klant"? (JP, punt 9 van 22-09.)
   *
   * Op Reviews staat de review zelf al bovenaan in `ReviewBlok`. Het
   * berichtblok viel daar terug op "Nog geen samenvatting van dit
   * bericht. Lees het origineel." — en er ís geen origineel bericht:
   * de review is het bericht. Een blok dat alleen zegt dat het leeg is,
   * is geen blok.
   */
  berichtblok?: boolean;
}

function leeftijdTekst(maanden: number | null): string | null {
  if (maanden === null) return null;
  if (maanden < 24) return `${maanden} maanden`;
  return `${Math.floor(maanden / 12)} jaar`;
}

/**
 * Woorden die geen naam zijn maar een plaatsaanduiding.
 *
 * Een dier zonder naam krijgt in de praktijk "Hond", "Tweede hond" of
 * "Hond 2" als naam. Dat leverde op het scherm "Hond Hond:" en "Hond
 * Tweede hond:" op (JP, punt 6 van 22-09).
 */
const NAAMLOOS_WOORDEN = new Set([
  "eerste",
  "tweede",
  "derde",
  "vierde",
  "nieuwe",
  "oude",
  "dier",
  "huisdier",
  "hond",
  "kat",
  "poes",
  "onbekend",
]);

/** Is dit een echte naam, of alleen een plaatsaanduiding? */
function isEchteNaam(naam: string | null | undefined): boolean {
  const woorden = (naam ?? "")
    .toLowerCase()
    .replace(/[^a-z\u00c0-\u024f0-9]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .filter((w) => !/^\d+$/.test(w));
  if (woorden.length === 0) return false;
  return woorden.some((w) => !NAAMLOOS_WOORDEN.has(w));
}

/**
 * "Hond" of "Hond Bo" — de soort met een hoofdletter, plus de naam als
 * het er een is.
 *
 * Eén keer de soort (JP, punt 6 van 22-09). Een dier zonder echte naam
 * heet gewoon "Hond"; "Hond Hond" en "Hond Tweede hond" zeggen niets
 * extra's en lezen als een fout.
 */
function dierSoortWoord(dier: ZaakDetail["dieren"][number]): string {
  const soort = (dier.diersoort ?? "").trim();
  const woord = soort ? soort[0].toUpperCase() + soort.slice(1) : "Dier";
  return isEchteNaam(dier.naam) ? `${woord} ${dier.naam}` : woord;
}

/**
 * "ras, leeftijd en gewicht onbekend, vraag na" — of wat er wél is.
 *
 * De ontbrekende velden staan op één plek bij elkaar in plaats van per
 * veld een streepje: dan lees je in één zin wat je nog moet vragen.
 */
function dierBeschrijving(dier: ZaakDetail["dieren"][number]): React.ReactNode {
  const bekend = [
    dier.ras,
    leeftijdTekst(dier.leeftijd_maanden),
    dier.gewicht_kg ? `${dier.gewicht_kg} kg` : null,
  ].filter(Boolean);
  return (
    <>
      {bekend.join(", ")}
      {bekend.length > 0 && dier.onbekend.length > 0 ? ", " : null}
      {dier.onbekend.length > 0 ? (
        <span className="wp-onb">
          {dier.onbekend.length === 1
            ? dier.onbekend[0]
            : `${dier.onbekend.slice(0, -1).join(", ")} en ${dier.onbekend.at(-1)}`}{" "}
          onbekend, vraag na
        </span>
      ) : null}
    </>
  );
}

function Klantblok({
  detail,
  bannerStaatEr,
  opVoeding,
}: {
  detail: ZaakDetail;
  /** Staat de koppelbanner er al? Dan zwijgt dit blok (JP, punt 21). */
  bannerStaatEr: boolean;
  /** Opent het rekenblok van de klantpagina in een paneel (punt 19). */
  opVoeding: () => void;
}) {
  const klant = detail.klant;
  const acties = useKlantActies();
  if (!klant) {
    // Twee keer "Geen klant gevonden" onder elkaar — het blok met de
    // koppelvoorstellen én een grijze balk erboven — is één keer te
    // veel. De banner zegt hetzelfde én biedt de knoppen, dus die wint.
    if (bannerStaatEr) return null;
    return (
      <div className="wp-blok">
        <div className="wp-blok-body" style={{ color: "var(--wp-tekst2)" }}>
          Geen klant gevonden bij deze zaak: koppelen of een nieuwe klant
          aanmaken.
        </div>
      </div>
    );
  }
  const sinds = klant.klant_sinds
    ? new Date(klant.klant_sinds).getFullYear()
    : null;
  function doe(kanaal: KlantKanaal) {
    if (!klant?.id) return;
    void acties.voerUit({
      kanaal,
      klantId: klant.id,
      zaakId: detail.zaak.id,
      zaakKanaal: detail.zaak.kanaal_hoofd,
      email: klant.email,
      nummer: klant.telefoon,
    });
  }
  return (
    <div className="wp-blok">
      <div className="wp-klantkop">
        <div className="wp-naam">
          {/* Naar de klantpagina van de werkplek. Die route bestaat
              sinds de merge van bouwer 4 (#631); daarvoor wees deze link
              naar het oude portaalscherm omdat een link naar een
              onbestaande route op een leeg scherm uitkomt. */}
          <a href={`/klanten/${klant.id}`} data-klantlink>
            {klant.naam}
          </a>
          <span className="wp-chip wp-chip-grijs">
            {sinds ? `Klant sinds ${sinds}` : "Klant sinds onbekend"}
          </span>
        </div>
        <div className="wp-acties">
          <button
            type="button"
            className="wp-knop"
            data-klantactie="telefoon"
            disabled={!klant.telefoon || acties.bezig}
            title={
              klant.telefoon
                ? "Bellen via Rinkel"
                : "Geen telefoonnummer bekend bij deze klant"
            }
            onClick={() => doe("telefoon")}
          >
            <IcoonTelefoon maat={13} />
            Bel
          </button>
          <button
            type="button"
            className="wp-knop"
            data-klantactie="mail"
            disabled={!klant.email || acties.bezig}
            title={klant.email ? "Mail deze klant" : "Geen e-mailadres bekend"}
            onClick={() => doe("mail")}
          >
            <IcoonMail maat={13} />
            Mail
          </button>
          <button
            type="button"
            className="wp-knop"
            data-klantactie="whatsapp"
            disabled={!klant.telefoon || acties.bezig}
            title={
              klant.telefoon
                ? "WhatsApp deze klant"
                : "Geen telefoonnummer bekend bij deze klant"
            }
            onClick={() => doe("whatsapp")}
          >
            <IcoonWhatsapp maat={13} />
            WhatsApp
          </button>
        </div>
        {acties.fout ? (
          <div className="wp-sub" role="alert">
            Kon geen gesprek klaarzetten: {acties.fout}
          </div>
        ) : null}
        {/* Sessie 171: bestellingen en klantwaarde van de klantgroep,
            dezelfde getallen als de kop van de klantkaart. */}
        <div className="wp-klantcijfers" data-testid="wp-klantcijfers">
          <span>
            <b>{klant.aantal_orders}</b>{" "}
            {klant.aantal_orders === 1 ? "bestelling" : "bestellingen"}
            {klant.laatste_order_op
              ? `, laatste ${datum(klant.laatste_order_op)}`
              : null}
          </span>
          <span>
            <b>{euro(klant.lifetime_value_cents)}</b> klantwaarde
          </span>
          <span>
            <b>{klant.aantal_retouren}</b>{" "}
            {klant.aantal_retouren === 1 ? "retour" : "retouren"}
          </span>
          <span>
            <b>{klant.open_zaken}</b>{" "}
            {klant.open_zaken === 1 ? "open zaak, deze" : "open zaken"}
          </span>
        </div>

        {/* Eén regel per dier (JP, punt 19 van 22-09). Alle dieren
            achter elkaar op één regel liep bij twee honden al over drie
            regels, en dan is niet meer te zien wat bij wie hoort. */}
        <div className="wp-dierregel">
          {detail.dieren.length === 0 ? (
            <span className="wp-onb">Geen dier bekend, vraag na</span>
          ) : (
            detail.dieren.map((dier, i) => (
              <span className="wp-dierrij" key={dier.naam + i}>
                <b>{dierSoortWoord(dier)}:</b>{" "}
                {dierBeschrijving(dier)}
              </span>
            ))
          )}
          <button
            type="button"
            className="wp-knop wp-dierknop"
            data-voedingsblok
            disabled={!klant.id}
            title={
              klant.id
                ? "Open het rekenblok van de klantpagina"
                : "Zonder klant is er geen dier om voor te rekenen"
            }
            onClick={() => opVoeding()}
          >
            Voedingsblok
          </button>
        </div>

        <div className="wp-sub">
          {[klant.adres, klant.telefoon, klant.email]
            .filter(Boolean)
            .join(" · ") || "Geen adresgegevens bekend"}
        </div>
      </div>
    </div>
  );
}

function Feit({
  label,
  zebra,
  children,
}: {
  label: string;
  zebra: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={zebra ? "wp-z" : undefined} data-feit={label.toLowerCase()}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function duurTekst(seconden: number | null): string | null {
  if (seconden == null) return null;
  if (seconden < 60) return `${seconden} s`;
  const minuten = Math.floor(seconden / 60);
  const rest = seconden % 60;
  return rest ? `${minuten} min ${rest} s` : `${minuten} min`;
}

/**
 * Sessie 48 (A3): richting, tijd, duur, opgenomen of gemist, doorverbonden
 * naar de antwoordservice, en de belnotitie. Eerst in De zaak, want bij een
 * telefoonzaak is dit het bericht van de klant.
 */
function TelefoonFeiten({
  telefoon,
  volgende,
}: {
  telefoon: ZaakTelefoon;
  volgende: () => boolean;
}) {
  const duur = duurTekst(telefoon.duur_seconden);
  const richting = telefoon.terugbelverzoek
    ? "Terugbelverzoek"
    : telefoon.richting === "uitgaand"
      ? "Uitgaand"
      : "Inkomend";
  return (
    <>
      <Feit label="Gesprek" zebra={volgende()}>
        <span data-telefoon-gesprek>
          {richting} · {datumTijd(telefoon.moment)}
          {duur ? ` · ${duur}` : null}
          {telefoon.nummer ? ` · ${telefoon.nummer}` : null}
          <br />
          <span
            data-telefoon-uitkomst={
              telefoon.opgenomen === true
                ? "opgenomen"
                : telefoon.opgenomen === false
                  ? "niet-opgenomen"
                  : "onbekend"
            }
          >
            {telefoon.uitkomst_tekst}
          </span>
        </span>
      </Feit>
      {telefoon.richting !== "uitgaand" ? (
        <Feit label="Doorverbonden" zebra={volgende()}>
          <span data-telefoon-doorverbonden={telefoon.doorverbonden ? "ja" : "nee"}>
            {telefoon.doorverbonden
              ? `Ja, naar ${telefoon.doorverbonden_naar ?? "de antwoordservice"}`
              : "Nee"}
          </span>
        </Feit>
      ) : null}
      {telefoon.notitie ? (
        <Feit label="Belnotitie" zebra={volgende()}>
          <span data-telefoon-notitie>
            {telefoon.notitie.verslag ?? "Geen verslag."}
            <br />
            <span className="wp-leegregel">
              {[
                telefoon.notitie.beller,
                telefoon.notitie.nummer,
                telefoon.notitie.ordernummer
                  ? `order ${telefoon.notitie.ordernummer}`
                  : null,
                telefoon.notitie.aangenomen_door
                  ? `aangenomen door ${telefoon.notitie.aangenomen_door}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
        </Feit>
      ) : null}
      {telefoon.terugbellen ? (
        <Feit label="Terugbellen" zebra={volgende()}>
          <span data-telefoon-terugbellen>
            {telefoon.terugbellen_reden ?? "Nog niet teruggebeld."}
          </span>
        </Feit>
      ) : null}
    </>
  );
}

/** Sessie 51: de tijdlijnregel van de eerste blik op een foto. */
export const SOORT_EERSTE_BLIK = "eerste_blik";

/** "Wat ik zie: ..." zonder het voorvoegsel; het label staat ervoor. */
export function watIkZieTekst(regel: TijdlijnRegel): string {
  return regel.tekst.replace(/^\s*wat ik zie:\s*/i, "");
}

function DeZaak({ detail }: { detail: ZaakDetail }) {
  const { order, verzending, zending, wekker, retour } = detail;
  let rij = 0;
  const volgende = () => rij++ % 2 === 0;
  // Sessie 51: "Wat ik zie" staat op de tijdlijn van de zaak (Nova's
  // eerste blik op een foto). Zelfde sleutel als de tegel ververst.
  const tijdlijn = useQuery({
    queryKey: ["werkplek", "tijdlijn", detail.zaak.id],
    queryFn: () => werkplekApi.tijdlijn(detail.zaak.id),
  });
  const blikken = (tijdlijn.data ?? []).filter(
    (regel) => regel.soort === SOORT_EERSTE_BLIK && !regel.teruggedraaid_op,
  );
  return (
    <div className="wp-blok">
      <h4>
        <span>De zaak</span>
        <span className="wp-koprechts">
          <ActiesMenu
            zaakId={detail.zaak.id}
            klantId={detail.klant?.id ?? detail.zaak.klant_id}
          />
        </span>
      </h4>
      <dl className="wp-feiten">
        {detail.telefoon ? (
          <TelefoonFeiten telefoon={detail.telefoon} volgende={volgende} />
        ) : null}
        {blikken.length > 0 ? (
          <Feit label="Wat ik zie" zebra={volgende()}>
            {blikken.map((regel) => (
              <div
                key={regel.id}
                className="wp-watikzie"
                data-wat-ik-zie={regel.id}
                style={{ display: "flex", gap: 6, alignItems: "flex-start" }}
              >
                <IcoonBeeld maat={13} />
                <span>
                  {watIkZieTekst(regel)}
                  {regel.details?.bruikbaar === false ? (
                    <span className="wp-leegregel">
                      {" "}
                      · foto niet goed bruikbaar
                    </span>
                  ) : null}
                  {" · "}
                  {datumTijd(regel.moment)}
                </span>
              </div>
            ))}
          </Feit>
        ) : null}
        <Feit label="Order" zebra={volgende()}>
          {order ? (
            <>
              {order.bron_label ? (
                <span
                  className="wp-chip wp-chip-vermoeden"
                  data-orderbron={order.bron}
                  title={
                    order.bron === "vermoedelijk"
                      ? "Er is geen order aan deze zaak gekoppeld; dit is de laatste order van de klant."
                      : "De klant noemt dit ordernummer zelf in het bericht."
                  }
                >
                  {order.bron_label}
                </span>
              ) : null}
              {order.portaal_url ? (
                <a href={order.portaal_url}>{order.nummer}</a>
              ) : (
                order.nummer
              )}
              {order.lightspeed_url ? (
                <a
                  className="wp-ext"
                  href={order.lightspeed_url}
                  target="_blank"
                  rel="noreferrer"
                  title="Open in Lightspeed (nieuw tabblad)"
                >
                  <IcoonExtern maat={11} />
                  Lightspeed
                </a>
              ) : null}
              <br />
              {order.besteld_op ? `${datumTijd(order.besteld_op)} · ` : null}
              {order.regels.length > 0 ? (
                order.regels
                  .map((r) => `${r.omschrijving} × ${r.aantal}`)
                  .join(", ")
              ) : (
                <span className="wp-leegregel" data-leeg="orderregels">
                  {order.regels_leeg_reden}
                </span>
              )}
              {" · "}
              {euro(order.bedrag_cents)}
              {order.betaalmethode ? ` · ${order.betaalmethode}` : null}
              {order.betaald ? ", betaald" : ", nog niet betaald"}
              {order.ook_open && order.ook_open.length > 0 ? (
                <>
                  <br />
                  <span className="wp-ookopen" data-ook-open>
                    Ook open:{" "}
                    {order.ook_open
                      .map((o) => `${o.nummer} (${o.stand})`)
                      .join(", ")}
                  </span>
                </>
              ) : null}
            </>
          ) : (
            <span className="wp-leegregel">
              Geen order gekoppeld aan deze zaak.
            </span>
          )}
        </Feit>

        <Feit label="Verzonden" zebra={volgende()}>
          {verzending ? (
            <>
              {verzending.partij ?? "Onbekende verzendpartij"}
              {verzending.moment ? `, ${datumTijd(verzending.moment)}` : null}
              {verzending.gewicht_kg ? ` · ${verzending.gewicht_kg} kg` : null}
              {verzending.dozen
                ? ` · ${verzending.dozen} ${verzending.dozen === 1 ? "doos" : "dozen"}`
                : null}
              {verzending.leeg_reden ? (
                <>
                  <br />
                  <span className="wp-leegregel" data-leeg="gewicht">
                    {verzending.leeg_reden}
                  </span>
                </>
              ) : null}
            </>
          ) : (
            <span className="wp-leegregel">Nog niet verzonden.</span>
          )}
        </Feit>

        <Feit label="Zending" zebra={volgende()}>
          {zending ? (
            <>
              {zending.vervoerder ?? "Onbekende vervoerder"}{" "}
              {zending.code ?? ""}
              {zending.track_url ? (
                <a
                  className="wp-ext"
                  href={zending.track_url}
                  target="_blank"
                  rel="noreferrer"
                  title="Track en trace (nieuw tabblad)"
                >
                  <IcoonExtern maat={11} />
                  Track en trace
                </a>
              ) : null}
              {zending.status ? (
                <>
                  <br />
                  {zending.status}
                </>
              ) : null}
              {zending.bezorgd_op ? (
                <> · bezorgd {datumTijd(zending.bezorgd_op)}</>
              ) : null}
              {zending.plek ? `, ${zending.plek}` : null}
              {zending.handtekening ? ", getekend" : null}
            </>
          ) : detail.zending_ophalen ? (
            <span className="wp-leegregel" data-leeg="zending-ophalen">
              Wordt opgehaald bij Picqer en de vervoerder…
            </span>
          ) : (
            <span className="wp-leegregel">Nog geen zending bekend.</span>
          )}
        </Feit>

        {retour ? (
          <>
            <Feit label="Reden" zebra={volgende()}>
              {retour.reden ?? (
                <span className="wp-leegregel">Geen reden vastgelegd.</span>
              )}
            </Feit>
            <Feit label="Label" zebra={volgende()}>
              {retour.label_beschikbaar ? (
                <RetourLabelOpenen
                  retourId={retour.retour_id ?? null}
                  tekst={retour.label ?? "Retourlabel"}
                />
              ) : (
                <span className="wp-leegregel">Nog geen retourlabel.</span>
              )}
            </Feit>
            <Feit label="Ontvangen" zebra={volgende()}>
              {retour.ontvangen_op ? (
                datumTijd(retour.ontvangen_op)
              ) : retour.binnen ? (
                "Binnen bij Cenfil, moment niet bekend"
              ) : (
                <span className="wp-leegregel">Nog niet terug ontvangen.</span>
              )}
            </Feit>
          </>
        ) : null}

        <Feit label="Regels" zebra={volgende()}>
          {detail.regels.length > 0 ? (
            detail.regels.map((regel, i) => (
              <div key={i}>
                {regel.tekst}
                {regel.beslist_door ? ` (${regel.beslist_door})` : null}
              </div>
            ))
          ) : (
            <span className="wp-leegregel" data-leeg="regels">
              {detail.regels_leeg_reden ?? "Geen regel van toepassing."}
            </span>
          )}
        </Feit>

        <Feit label="Case" zebra={volgende()}>
          {detail.case ? (
            <>
              {detail.case.ticket_nummer ?? "Case aangemaakt"}
              {detail.case.status ? ` · ${detail.case.status}` : null}
            </>
          ) : (
            <span className="wp-leegregel">
              Nog geen case bij de vervoerder. Staat in Acties.
            </span>
          )}
        </Feit>

        <Feit label="Wekker" zebra={volgende()}>
          {wekker ? (
            <>
              <span className="wp-chip wp-chip-wekker">
                {wekker.status === "afgelopen" ? "afgelopen" : "loopt"}
              </span>{" "}
              {wekkerReden(wekker.reden)} · {datumTijd(wekker.afloopt_op)}
            </>
          ) : (
            <span className="wp-leegregel" data-leeg="wekker">
              Geen.{" "}
              <button
                type="button"
                className="wp-linkknop"
                data-zet-wekker
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("wp-acties-open", {
                      detail: { zaakId: detail.zaak.id, soort: "wekker_zetten" },
                    }),
                  )
                }
              >
                Zet er een
              </button>
            </span>
          )}
        </Feit>
      </dl>
    </div>
  );
}

function Bericht({
  detail,
  opPaneel,
  opFoto,
}: {
  detail: ZaakDetail;
  opPaneel: (welke: PaneelSoort) => void;
  opFoto: (url: string, naam: string) => void;
}) {
  const bericht = detail.bericht;
  if (!bericht) return null;
  return (
    <div className="wp-blok">
      <h4>
        Bericht van de klant
        <span className="wp-koprechts">
          <span className="wp-chip wp-chip-grijs">
            {(bericht.ballonnen ?? []).length > 0 ? "gesprek" : "samenvatting"}
          </span>
        </span>
      </h4>
      {/* Een gesprek lees je als gesprek (JP, punt 15 van 22-09):
          klant links, wij rechts, het moment eronder. Alleen als er
          geen beurten zijn valt het blok terug op de samenvatting —
          en dat is bij mail altijd. */}
      {(bericht.ballonnen ?? []).length > 0 ? (
        <div className="wp-ballonnen" data-ballonnen>
          {(bericht.ballonnen ?? []).map((ballon) => (
            <div
              key={ballon.id}
              className="wp-ballon"
              data-kant={ballon.kant}
            >
              {ballon.tekst ? (
                <span className="wp-ballon-tekst">{ballon.tekst}</span>
              ) : null}
              {/* Sessie 52b: spraak uitgeschreven en foto's met wat
                  Nova zag, en bij ons bericht de verzendstatus als
                  woord (punt 4 van 26-09). */}
              <BallonExtra ballon={ballon} opFoto={opFoto} />
              <span className="wp-ballon-voet">
                {ballon.wie} · {klok(ballon.moment)}
                <VerzendStatus status={ballon.verzendstatus} />
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="wp-samenvatting">
          {bericht.samenvatting ? (
            bericht.samenvatting
              .split(/\n{2,}/)
              .map((alinea, i) => <p key={i}>{alinea}</p>)
          ) : (
            <p className="wp-leegregel">
              Nog geen samenvatting van dit bericht. Lees het origineel.
            </p>
          )}
        </div>
      )}

      {bericht.bijlagen.length > 0 ? (
        <div className="wp-bijlagen">
          <div className="wp-fotos">
            {bericht.bijlagen.map((bijlage) => (
              <button
                key={bijlage.id}
                type="button"
                className="wp-foto"
                data-bijlage={bijlage.id}
                onClick={() =>
                  bijlage.volledig_url &&
                  opFoto(bijlage.volledig_url, bijlage.naam)
                }
              >
                {bijlage.thumbnail_url &&
                (bijlage.soort ?? "").startsWith("image/") ? (
                  <img src={bijlage.thumbnail_url} alt={bijlage.naam} />
                ) : (
                  <span style={{ position: "static" }}>{bijlage.naam}</span>
                )}
                {bijlage.thumbnail_url &&
                (bijlage.soort ?? "").startsWith("image/") ? (
                  <span>{bijlage.naam}</span>
                ) : null}
              </button>
            ))}
          </div>
          <div className="wp-gelezen">
            {bericht.ai_bijlagen_regel ? (
              <>
                <b>AI heeft de bijlagen bekeken:</b> {bericht.ai_bijlagen_regel}
              </>
            ) : (
              <span className="wp-leegregel">
                De AI heeft deze bijlagen nog niet bekeken.
              </span>
            )}
          </div>
        </div>
      ) : null}

      <div className="wp-berichtknoppen">
        <button
          type="button"
          className="wp-knop"
          data-paneel="origineel"
          onClick={() => opPaneel("origineel")}
        >
          <IcoonMail maat={13} />
          Lees het origineel
        </button>
        <button
          type="button"
          className="wp-knop"
          data-paneel="eerder"
          disabled={bericht.eerdere_gesprekken === 0}
          title={
            bericht.eerdere_gesprekken === 0
              ? "Deze klant heeft nog geen eerdere gesprekken"
              : undefined
          }
          onClick={() => opPaneel("eerder")}
        >
          Eerdere gesprekken ({bericht.eerdere_gesprekken})
        </button>
      </div>
    </div>
  );
}

function PaneelOrigineel({
  zaakId,
  sluit,
}: {
  zaakId: string;
  sluit: () => void;
}) {
  const vraag = useQuery({
    queryKey: ["werkplek", "origineel", zaakId],
    queryFn: () => kaartenApi.origineel(zaakId),
  });
  return (
    <div
      className="wp-paneel-in"
      data-paneel-open="origineel"
      role="dialog"
      aria-label="Het originele bericht"
    >
      <header>
        <h3>Lees het origineel</h3>
        <button
          type="button"
          className="wp-knop"
          onClick={sluit}
          data-paneel-sluit
        >
          <IcoonKruis maat={13} />
        </button>
      </header>
      <div className="wp-paneel-body">
        {vraag.isLoading ? (
          "Bezig met laden…"
        ) : vraag.isError ? (
          <span role="alert">
            Het originele bericht is niet op te halen bij deze zaak.
          </span>
        ) : (
          <div className="wp-origineel">
            <div className="wp-origineel-kop">
              {vraag.data?.van}
              {vraag.data?.moment ? ` · ${datumTijd(vraag.data.moment)}` : null}
              {vraag.data?.onderwerp ? `\n${vraag.data.onderwerp}` : null}
            </div>
            {vraag.data?.tekst ?? "Dit bericht heeft geen platte tekst."}
          </div>
        )}
      </div>
    </div>
  );
}

function PaneelEerder({
  zaakId,
  sluit,
}: {
  zaakId: string;
  sluit: () => void;
}) {
  const vraag = useQuery({
    queryKey: ["werkplek", "eerder", zaakId],
    queryFn: () => kaartenApi.eerdereGesprekken(zaakId),
  });
  return (
    <div
      className="wp-paneel-in"
      data-paneel-open="eerder"
      role="dialog"
      aria-label="Eerdere gesprekken"
    >
      <header>
        <h3>Eerdere gesprekken ({vraag.data?.length ?? 0})</h3>
        <button
          type="button"
          className="wp-knop"
          onClick={sluit}
          data-paneel-sluit
        >
          <IcoonKruis maat={13} />
        </button>
      </header>
      <div className="wp-paneel-body">
        {vraag.isLoading ? (
          "Bezig met laden…"
        ) : vraag.isError ? (
          <span role="alert">De eerdere gesprekken zijn niet op te halen.</span>
        ) : (vraag.data ?? []).length === 0 ? (
          "Deze klant heeft geen eerdere gesprekken."
        ) : (
          (vraag.data ?? []).map((gesprek) => (
            <details key={gesprek.id} className="wp-draadje">
              <summary>
                <span
                  className={
                    // Sessie 46 (A10): retouren en reviews staan er ook
                    // tussen; die hebben geen kanaalkleur.
                    gesprek.kanaal === "retour" || gesprek.kanaal === "review"
                      ? "wp-chip wp-chip-grijs"
                      : `wp-chip wp-chip-${gesprek.kanaal === "email" ? "mail" : gesprek.kanaal}`
                  }
                >
                  {gesprek.kanaal}
                </span>
                {gesprek.onderwerp ?? "Zonder onderwerp"}
                <span
                  style={{
                    marginLeft: "auto",
                    color: "var(--wp-tekst3)",
                    fontSize: 11,
                  }}
                >
                  {datum(gesprek.moment)} {klok(gesprek.moment)}
                </span>
              </summary>
              <div className="wp-draadje-body">
                {gesprek.samenvatting ?? "Geen samenvatting van dit gesprek."}
              </div>
            </details>
          ))
        )}
      </div>
    </div>
  );
}

function PaneelVoeding({
  klantId,
  zaakId,
  sluit,
}: {
  klantId: string;
  zaakId: string | null;
  sluit: () => void;
}) {
  const klant = useQuery({
    queryKey: ["werkplek", "klant", klantId],
    queryFn: () => werkplekKlantenApi.pagina(klantId),
  });
  // Sessie 47 (6a): geen dier? Dan opent "Dier toevoegen" meteen, vooraf
  // ingevuld met wat de klant in het bericht noemde (status aanname).
  const zonderDier = klant.data !== undefined && klant.data.dieren.length === 0;
  const voorstel = useQuery({
    queryKey: ["werkplek", "dier-voorstel", zaakId],
    queryFn: () => kaartenApi.dierVoorstel(zaakId as string),
    enabled: Boolean(zaakId) && zonderDier,
    staleTime: 10 * 60_000,
  });
  return (
    <div
      className="wp-paneel-in"
      data-paneel-open="voeding"
      role="dialog"
      aria-label="Voedingsblok"
    >
      <header>
        <h3>Voedingsblok</h3>
        <button
          type="button"
          className="wp-knop"
          onClick={sluit}
          data-paneel-sluit
        >
          <IcoonKruis maat={13} />
        </button>
      </header>
      <div className="wp-paneel-body">
        {klant.isLoading ? (
          "Bezig met laden…"
        ) : klant.isError || !klant.data ? (
          <span role="alert">
            De gegevens van deze klant zijn niet op te halen, dus er valt
            niets te berekenen.
          </span>
        ) : (
          <>
            {/* Hetzelfde blok als op de klantpagina (sessie 46, B9). */}
            <DierenBeheer
              klantId={klantId}
              dieren={klant.data.dieren}
              voorstel={voorstel.data ?? null}
              // Pas openen als het voorstel binnen is: het formulier leest
              // het alleen bij het openen, en een later voorstel zou er
              // anders nooit meer in komen.
              meteenToevoegen={zonderDier && !voorstel.isLoading}
            />
            <VoedingsBlok klantId={klantId} dieren={klant.data.dieren} />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Sessie 169: het retourlabel komt als PDF via de admin-route (Bearer),
 * nooit als ruwe URL van de vervoerder. Zonder retour-id in de kaart
 * staat er alleen de tekst.
 */
function RetourLabelOpenen({
  retourId,
  tekst,
}: {
  retourId: string | null;
  tekst: string;
}) {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  if (!retourId) return <span data-label-tekst>{tekst}</span>;
  return (
    <>
      <button
        type="button"
        className="wp-linkknop"
        data-testid="wp-kaart-label-knop"
        disabled={bezig}
        onClick={() => {
          setFout(null);
          setBezig(true);
          openPdf(
            () => adminRetourenApi.labelPdf(retourId),
            "retourlabel.pdf",
            "Het label kon niet worden geopend",
          )
            .catch((e: unknown) =>
              setFout(e instanceof Error ? e.message : String(e)),
            )
            .finally(() => setBezig(false));
        }}
      >
        {bezig ? "Bezig…" : tekst}
      </button>
      {fout ? (
        <span className="wp-leegregel" role="alert">
          {" "}
          {fout}
        </span>
      ) : null}
    </>
  );
}

export function Middenkolom({
  zaakId,
  delen,
}: {
  zaakId: string | null;
  delen?: MiddenkolomDelen;
}) {
  const toonKop = delen?.kop ?? true;
  const toonKlantblok = delen?.klantblok ?? true;
  const toonZaakblok = delen?.zaakblok ?? true;
  const toonBerichtblok = delen?.berichtblok ?? true;
  const [paneel, setPaneel] = useState<PaneelSoort>(null);
  const [foto, setFoto] = useState<{ url: string; naam: string } | null>(null);

  const vraag = useQuery({
    queryKey: ["werkplek", "zaak", zaakId],
    queryFn: () => kaartenApi.detail(zaakId as string),
    enabled: Boolean(zaakId),
    // Sessie 47 (4a): de zending wordt op de achtergrond opgehaald bij
    // Picqer en de vervoerder; kijk over vijf seconden nog een keer.
    refetchInterval: (query) =>
      query.state.data?.zending_ophalen ? 5000 : false,
  });

  // Dezelfde vraag als in `KoppelBanner`: react-query deelt hem op de
  // sleutel, dus dit kost geen tweede aanroep. Nodig om te weten of de
  // banner er staat — anders staat "Geen klant gevonden" er twee keer
  // (JP, punt 21 van 22-09).
  const banner = useKoppelBanner(zaakId);
  const bannerStaatEr = Boolean(banner.data?.nodig);

  // Een andere kaart sluit alles wat eroverheen lag.
  useEffect(() => {
    setPaneel(null);
    setFoto(null);
  }, [zaakId]);

  useEffect(() => {
    if (!paneel && !foto) return;
    function ontsnap(gebeurtenis: KeyboardEvent) {
      if (gebeurtenis.key !== "Escape") return;
      if (foto) setFoto(null);
      else setPaneel(null);
    }
    window.addEventListener("keydown", ontsnap);
    return () => window.removeEventListener("keydown", ontsnap);
  }, [paneel, foto]);

  return (
    <>
      <div className="wp-scrol">
        {!zaakId ? (
          <div className="wp-leeg" style={{ marginTop: 40 }}>
            Kies een kaart links. De zaak verschijnt hier met het klantblok, de
            feiten en het bericht van de klant.
          </div>
        ) : vraag.isLoading ? (
          <div className="wp-leeg">Bezig met laden…</div>
        ) : vraag.isError || !vraag.data ? (
          <div className="wp-leeg" role="alert">
            Deze zaak is niet op te halen. Ververs de pagina; blijft dit staan,
            dan is de API niet bereikbaar.
          </div>
        ) : (
          <>
            {toonKop ? (
              <div className="wp-b-kop">
                <h3>{vraag.data.zaak.onderwerp ?? "Zaak zonder onderwerp"}</h3>
              </div>
            ) : null}
            {toonKlantblok ? <KoppelBanner zaakId={zaakId} /> : null}
            {/* Onder elkaar, zoals het ontwerp voorschrijft (§3). Naast
                elkaar geprobeerd op 22-09 om ruimte te winnen: dat
                werkt niet. De middenkolom is op 1440 px maar 520 px
                breed, en in twee kolommen van 230/280 px wrappen de
                blokken zó dat ze samen 425 px worden in plaats van de
                289 px die naast elkaar zou moeten opleveren — hoger dan
                de 408 px die ze gestapeld zijn. Gemeten, niet geschat. */}
            {toonKlantblok ? (
              <Klantblok
                detail={vraag.data}
                bannerStaatEr={bannerStaatEr}
                opVoeding={() => setPaneel("voeding")}
              />
            ) : null}
            {toonZaakblok ? <DeZaak detail={vraag.data} /> : null}
            {toonZaakblok && vraag.data.schadedossier ? (
              <SchadeBlok
                dossier={vraag.data.schadedossier}
                opFoto={(url, naam) => setFoto({ url, naam })}
              />
            ) : null}
            {toonBerichtblok ? (
              <Bericht
                detail={vraag.data}
                opPaneel={setPaneel}
                opFoto={(url, naam) => setFoto({ url, naam })}
              />
            ) : null}
          </>
        )}
      </div>

      {zaakId && paneel === "origineel" ? (
        <PaneelOrigineel zaakId={zaakId} sluit={() => setPaneel(null)} />
      ) : null}
      {zaakId && paneel === "eerder" ? (
        <PaneelEerder zaakId={zaakId} sluit={() => setPaneel(null)} />
      ) : null}
      {/* Het rekenblok van de klantpagina, hier als paneel over de
          kolom — net als de andere twee panelen (JP, punt 19). */}
      {paneel === "voeding" && vraag.data?.klant?.id ? (
        <PaneelVoeding
          klantId={vraag.data.klant.id}
          zaakId={zaakId}
          sluit={() => setPaneel(null)}
        />
      ) : null}

      {foto ? (
        <div
          className="wp-fotolaag"
          data-fotolaag
          role="dialog"
          aria-label={foto.naam}
        >
          <img src={foto.url} alt={foto.naam} />
          <button
            type="button"
            className="wp-knop"
            onClick={() => setFoto(null)}
          >
            <IcoonKruis maat={13} />
            Sluiten
          </button>
        </div>
      ) : null}
    </>
  );
}
